import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { BaseHttpService } from '@shared/services/http/base-http.service';
import { HttpClient } from '@angular/common/http';
import { TableStructure } from '@shared/components/table/models/table-structure.model';
import {
  OrthodonticQaEvaluatorReviewsRecord,
  QaEvaluatorReviewType,
  QaEvaluatorReviewTypes,
  SpeechQaEvaluatorReviewsRecord,
  SurgicalQaEvaluatorReliabilityReviewsRecord,
  SurgicalQaEvaluatorReviewsRecord
} from './qa-evaluator-reviews-report.model';
import { AuthService } from '@src/app/features/authentication/services/auth.service';
import { PermissionEnum } from '@core/permissions/permission.enum';
import { WsHelperService } from '@shared/services/ws-helper.service';

@Injectable({
  providedIn: 'root'
})
export class QaEvaluatorReviewsReportService extends BaseHttpService {
  httpClient: HttpClient;
  private authService = inject(AuthService);
  private readonly wsHelperService = inject(WsHelperService);

  private static readonly apiUrlsMap: Record<QaEvaluatorReviewType, string> = {
    orthodontic: '/qa/evaluator-reviews/ortho',
    speechAssessment: '/qa/evaluator-reviews/speech_assessment',
    surgical: '/qa/evaluator-reviews/surgical',
    surgicalReliability: '/qa/evaluator-trial-reviews/surgical'
  };
  surgicalReviewList$: Observable<TableStructure<SurgicalQaEvaluatorReviewsRecord>>;
  surgicalReliabilityReviewList$: Observable<TableStructure<SurgicalQaEvaluatorReliabilityReviewsRecord>>;
  speechReviewList$: Observable<TableStructure<SpeechQaEvaluatorReviewsRecord>>;
  orthodonticReviewList$: Observable<TableStructure<OrthodonticQaEvaluatorReviewsRecord>>;
  surgicalReviewDataReady = false;
  speechReviewDataReady = false;
  orthodonticReviewDataReady = false;
  surgicalReliabilityDataReady = false;
  private surgicalReviewListSubject: Subject<TableStructure<SurgicalQaEvaluatorReviewsRecord>> = new Subject();
  private surgicalReliabilityReviewListSubject: Subject<TableStructure<SurgicalQaEvaluatorReliabilityReviewsRecord>> = new Subject();
  private speechReviewListSubject: Subject<TableStructure<SpeechQaEvaluatorReviewsRecord>> = new Subject();
  private orthodonticReviewListSubject: Subject<TableStructure<OrthodonticQaEvaluatorReviewsRecord>> = new Subject();

  constructor() {
    const httpClient = inject(HttpClient);
    super(httpClient, { baseUrl: 'reports' });
    this.httpClient = httpClient;
    this.surgicalReviewList$ = this.surgicalReviewListSubject.asObservable();
    this.surgicalReliabilityReviewList$ = this.surgicalReliabilityReviewListSubject.asObservable();
    this.speechReviewList$ = this.speechReviewListSubject.asObservable();
    this.orthodonticReviewList$ = this.orthodonticReviewListSubject.asObservable();
  }

  prepareLists(rangeParam: string) {
    this.surgicalReviewDataReady = false;
    this.speechReviewDataReady = false;
    this.orthodonticReviewDataReady = false;
    this.surgicalReliabilityDataReady = false;
    if (this.authService.checkUserPermission(PermissionEnum.GET_REPORT_QA_EVALUATOR_REVIEWS_SURGERY)) {
      this.prepareSurgicalReviewHistory(rangeParam);
      this.prepareSurgicalReliabilityReviewHistory(rangeParam);
    }
    if (this.authService.checkUserPermission(PermissionEnum.GET_REPORT_QA_EVALUATOR_REVIEWS_ORTHO)) {
      this.prepareOrthodonticReviewHistory(rangeParam);
    }
    if (this.authService.checkUserPermission(PermissionEnum.GET_REPORT_QA_EVALUATOR_REVIEWS_SPEECH)) {
      this.prepareSpeechAssessmentReviewHistory(rangeParam);
    }
  }

  prepareSurgicalReviewHistory(rangeParam: string) {
    const url = this.getApiUrl(QaEvaluatorReviewTypes.SURGICAL);

    this.wsHelperService
      .callWithSpinner(this.httpClient.get<TableStructure<SurgicalQaEvaluatorReviewsRecord>>(url, { params: { range: rangeParam } }))
      .subscribe({
        next: (response: TableStructure<SurgicalQaEvaluatorReviewsRecord>) => {
          this.surgicalReviewListSubject.next(response);
          this.surgicalReviewDataReady = true;
        },
        error: () => {
          this.surgicalReviewDataReady = true;
        }
      });
  }

  prepareSurgicalReliabilityReviewHistory(rangeParam: string) {
    const url = this.getApiUrl(QaEvaluatorReviewTypes.SURGICAL_RELIABILITY);

    this.wsHelperService
      .callWithSpinner(
        this.httpClient.get<TableStructure<SurgicalQaEvaluatorReliabilityReviewsRecord>>(url, { params: { range: rangeParam } })
      )
      .subscribe({
        next: (response: TableStructure<SurgicalQaEvaluatorReliabilityReviewsRecord>) => {
          this.surgicalReliabilityReviewListSubject.next(response);
          this.surgicalReliabilityDataReady = true;
        },
        error: () => {
          this.surgicalReliabilityDataReady = true;
        }
      });
  }

  prepareSpeechAssessmentReviewHistory(rangeParam: string) {
    const url = this.getApiUrl(QaEvaluatorReviewTypes.SPEECH_ASSESSMENT);

    this.wsHelperService
      .callWithSpinner(this.httpClient.get<TableStructure<SpeechQaEvaluatorReviewsRecord>>(url, { params: { range: rangeParam } }))
      .subscribe({
        next: (response: TableStructure<SpeechQaEvaluatorReviewsRecord>) => {
          this.speechReviewListSubject.next(response);
          this.speechReviewDataReady = true;
        },
        error: () => {
          this.speechReviewDataReady = true;
        }
      });
  }

  prepareOrthodonticReviewHistory(rangeParam: string) {
    const url = this.getApiUrl(QaEvaluatorReviewTypes.ORTHODONTIC);

    this.wsHelperService
      .callWithSpinner(this.httpClient.get<TableStructure<OrthodonticQaEvaluatorReviewsRecord>>(url, { params: { range: rangeParam } }))
      .subscribe({
        next: (response: TableStructure<OrthodonticQaEvaluatorReviewsRecord>) => {
          this.orthodonticReviewDataReady = true;
          this.orthodonticReviewListSubject.next(response);
        },
        error: () => {
          this.orthodonticReviewDataReady = true;
        }
      });
  }

  private getApiUrl(type: QaEvaluatorReviewType) {
    return `${this.baseUrl}${QaEvaluatorReviewsReportService.apiUrlsMap[type]}`;
  }
}
